import React, { useContext } from 'react';
import type { ELSModalService } from '@els/els-component-modal-react';
import { ELSButton } from '@els/els-component-button-react';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { ELSIcon } from '@els/els-component-form-field-react';
import {
  ELSButtonIconPrefix,
  ELSButtonType
} from '../../models/button.models';
import {
  RecommendationContentTypeDto,
  RecommendationFeedbackDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RemediationContentTypeConfigMap } from '../../models/remediation.models';
import { AppContext } from '../app/AppProvider.component';
import {
  submitRecommendationFeedbackAction,
  updateRecommendationFeedbackAction
} from '../app/app-state-actions.utilities';

export const CloseRecommendationPlayerModalId = 'CloseRecommendationPlayerModalId';

export type CloseRecommendationPlayerModalProps = {
  modalService: ELSModalService;
  onConfirm: () => void;
  recommendationId: number;
  recommendationFeedback: RecommendationFeedbackDto;
  recommendationContentType: RecommendationContentTypeDto;
  isForSubmission: boolean;
};

export type CloseRecommendationPlayerModalState = {
  isRelevant: boolean;
}

const CloseRecommendationPlayerModal = (props: CloseRecommendationPlayerModalProps): JSX.Element => {
  const {
    modalService,
    onConfirm,
    recommendationId,
    recommendationFeedback,
    recommendationContentType,
    isForSubmission,
  } = props;

  const { appState, setAppState, appStateRef } = useContext(AppContext);

  const [state, setState] = React.useState<CloseRecommendationPlayerModalState>({
    isRelevant: recommendationFeedback?.isRelevant ?? null,
  });

  const handleConfirm = () => {
    if (state.isRelevant !== null && state.isRelevant !== recommendationFeedback?.isRelevant) {
      const dto: RecommendationFeedbackDto = { recommendationId, isRelevant: state.isRelevant };
      if (recommendationFeedback) {
        updateRecommendationFeedbackAction({ appState, setAppState, appStateRef, dto });
      } else {
        submitRecommendationFeedbackAction({ appState, setAppState, appStateRef, dto });
      }
    }
    modalService.closeModal(CloseRecommendationPlayerModalId);
    onConfirm();
  };

  const handleCancel = () => {
    modalService.closeModal(CloseRecommendationPlayerModalId);
  };

  const handleRatingSelect = (isRelevant: boolean) => {
    setState(() => ({ isRelevant }));
  };

  const getModalTitle = () => {
    return isForSubmission
        ? `Submit ${RemediationContentTypeConfigMap[recommendationContentType].titleSingular.toLowerCase()}?`
        : `Close ${RemediationContentTypeConfigMap[recommendationContentType].titleSingular.toLowerCase()}?`;
  };

  const getModalDescription = () => {
    return isForSubmission
        ? `This ${RemediationContentTypeConfigMap[recommendationContentType].titleSingular.toLowerCase()} will be submitted and marked as completed.`
        : `This ${RemediationContentTypeConfigMap[recommendationContentType].titleSingular.toLowerCase()} will be closed without being marked as completed. Your progress will be saved automatically, and you can resume at any time.`;
  };

  const modalStyle = isForSubmission ? 'c-els-modal--secondary' : 'c-els-modal--negative';

  return (
    <div>
      <div className={`c-els-modal ${modalStyle}`}>
        <div className="c-els-modal__window u-els-width-1o3 u-els-width-1o1@mobile">
          <div>
            <button
                type="button"
                className="c-els-modal__close u-els-anchorize"
                onClick={() => {
                  modalService.closeModal(CloseRecommendationPlayerModalId);
                }}
            >
              <ELSIcon name="close" size="1x" />
              <span className="u-els-hide-visually">Close this modal window</span>
            </button>
            <h3>{getModalTitle()}</h3>
            <div className="u-els-margin-top-1x">
              {getModalDescription()}
            </div>
            <FlexLayout
                classes={['c-nra-complete-player-modal__feedback-section']}
                modifiers={[FlexLayoutModifier.LEFT]}
            >
              <FlexItem>
                <span className="u-els-color-n8">Was this activity relevant to your exam?</span>
              </FlexItem>
              <FlexItem classes={['c-nra-complete-player-modal__feedback-button']}>
                <button
                  type="button"
                  disabled={state.isRelevant === true}
                  className={`u-els-anchorize ${state.isRelevant === true ? 'c-nra-complete-player-modal__feedback-button--selected' : ''}`}
                  onClick={() => handleRatingSelect(true)}
                  aria-label="Yes, this activity was relevant"
                  aria-pressed={state.isRelevant === true}
                >
                  <ELSIcon name="thumbs-up" prefix={ELSButtonIconPrefix.GIZMO} />
                </button>
              </FlexItem>
              <FlexItem classes={['c-nra-complete-player-modal__feedback-button']}>
                <button
                  type="button"
                  disabled={state.isRelevant === false}
                  className={`u-els-anchorize ${state.isRelevant === false ? 'c-nra-complete-player-modal__feedback-button--selected' : ''}`}
                  onClick={() => handleRatingSelect(false)}
                  aria-label="No, this activity was not relevant"
                  aria-pressed={state.isRelevant === false}
                >
                  <ELSIcon name="thumbs-down" prefix={ELSButtonIconPrefix.GIZMO} />
                </button>
              </FlexItem>
            </FlexLayout>

            <FlexLayout modifiers={[FlexLayoutModifier.RIGHT, FlexLayoutModifier.WRAP]}>
              <FlexItem classes={['c-nra-complete-player-modal__close-button']}>
                <ELSButton type={ELSButtonType.SECONDARY} onClick={handleCancel}>Cancel</ELSButton>
              </FlexItem>
              <FlexItem classes={['c-nra-complete-player-modal__close-button']}>
                <ELSButton onClick={handleConfirm}>Confirm</ELSButton>
              </FlexItem>
            </FlexLayout>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloseRecommendationPlayerModal;
